<template>
  <div class="study-record-page">
    <!-- 头部装饰 -->
    <div class="header-decoration">
      <div class="trophy">🏆</div>
      <div class="star star-1">⭐</div>
      <div class="star star-2">✨</div>
    </div>

    <!-- 统计概览 -->
    <div class="stats-overview">
      <h1 class="page-title">我的学习记录</h1>
      <div class="stats-cards">
        <div class="stat-card">
          <div class="stat-icon-bg">📅</div>
          <div class="stat-content">
            <span class="stat-value">{{ studyDays }}</span>
            <span class="stat-label">学习天数</span>
          </div>
        </div>
        <div class="stat-card">
          <div class="stat-icon-bg">📝</div>
          <div class="stat-content">
            <span class="stat-value">{{ totalRecords }}</span>
            <span class="stat-label">练习次数</span>
          </div>
        </div>
        <div class="stat-card highlight">
          <div class="stat-icon-bg">🏆</div>
          <div class="stat-content">
            <span class="stat-value">{{ avgScore }}</span>
            <span class="stat-label">平均分</span>
          </div>
        </div>
      </div>
    </div>

    <!-- 今日打卡 -->
    <div class="check-in-section" v-if="todayRecords.length > 0">
      <div class="section-header">
        <span class="section-icon">🌟</span>
        <h2>今日打卡</h2>
      </div>
      <div class="check-in-card">
        <div class="progress-ring">
          <div class="ring-inner">
            <span class="progress-value">{{ todayRecords.length }}</span>
            <span class="progress-label">次练习</span>
          </div>
        </div>
        <div class="check-in-info">
          <p class="encourage-text">{{ encourageText }}</p>
          <div class="streak-badge">
            <span>🔥 连续打卡 {{ streakDays }} 天</span>
          </div>
        </div>
      </div>
    </div>

    <!-- 历史记录 -->
    <div class="history-section">
      <div class="section-header">
        <span class="section-icon">📚</span>
        <h2>练习历史</h2>
        <span class="record-count">共 {{ totalRecords }} 条</span>
      </div>

      <div class="record-list" v-if="records.length > 0">
        <div class="list-title">
          <span>单词练习</span>
          <span v-if="hiddenWordRecordCount > 0">最近 {{ wordPreviewLimit }} 条</span>
        </div>
        <div
          v-for="record in displayedWordRecords"
          :key="record.id"
          class="record-item"
          :class="{ synced: record.synced }"
          @click="goToWordRecord(record.id)"
        >
          <div class="record-main">
            <div class="word-section">
              <span class="book-badge">{{ record.bookTitle }}</span>
              <h3 class="word-title">{{ record.word }}</h3>
              <p class="record-summary">查看练习详情</p>
            </div>
            <div class="score-section">
              <div
                class="score-badge"
                :style="{ background: getScoreColor(record.score) }"
              >
                {{ record.score }}
              </div>
            </div>
          </div>
          <div class="record-meta">
            <span class="record-time">{{ formatTime(record.createTime) }}</span>
            <span class="sync-status">
              详情 ›
            </span>
          </div>
        </div>
        <button
          v-if="records.length > wordPreviewLimit"
          class="toggle-list-btn"
          @click="toggleWordRecords"
        >
          {{ showAllWordRecords ? '收起单词记录' : `展开全部 ${records.length} 条单词记录` }}
        </button>
      </div>

      <div class="record-list story-list" v-if="storyRecords.length > 0">
        <div class="list-title">绘本跟读</div>
        <div
          v-for="record in storyRecords"
          :key="record.studyRecordId"
          class="record-item story-record"
          @click="goToStoryRecord(record.studyRecordId)"
        >
          <div class="record-main">
            <div class="word-section">
              <span class="book-badge story-badge">{{ record.bookTitle }}</span>
              <h3 class="word-title">完成 {{ record.lineCount }} 句跟读</h3>
              <p class="record-summary">查看每句得分</p>
            </div>
            <div class="score-section">
              <div
                class="score-badge"
                :style="{ background: getScoreColor(record.totalScore) }"
              >
                {{ record.totalScore }}
              </div>
            </div>
          </div>
          <div class="record-meta">
            <span class="record-time">{{ formatTime(record.completedAt) }}</span>
            <span class="sync-status">详情 ›</span>
          </div>
        </div>
      </div>

      <!-- 空状态 -->
      <div v-if="totalRecords === 0" class="empty-state">
        <div class="empty-illustration">📖</div>
        <h3>还没有练习记录</h3>
        <p>快去绘本页面开始你的第一次练习吧！</p>
        <button class="start-btn" @click="goToBooks">
          开始学习
        </button>
      </div>
    </div>

    <!-- 清空记录按钮 -->
    <div v-if="records.length > 0" class="clear-section">
      <button class="clear-btn" @click="clearAllRecords">
        清空所有记录
      </button>
    </div>

    <!-- 底部导航 -->
    <nav class="bottom-nav">
      <div class="nav-item" @click="goToBooks">
        <svg class="nav-icon" viewBox="0 0 24 24" aria-hidden="true">
          <path d="M12 3l9 8v10a1 1 0 0 1-1 1h-5v-6H9v6H4a1 1 0 0 1-1-1V11l9-8z" fill="currentColor" />
        </svg>
        <span class="nav-text">首页</span>
      </div>
      <div class="nav-item active">
        <svg class="nav-icon" viewBox="0 0 24 24" aria-hidden="true">
          <path d="M12 12a5 5 0 1 0-5-5 5 5 0 0 0 5 5zm0 2c-4.42 0-8 2.24-8 5v1h16v-1c0-2.76-3.58-5-8-5z" fill="currentColor" />
        </svg>
        <span class="nav-text">我的</span>
      </div>
    </nav>

    <!-- 装饰元素 -->
    <div class="floating-elements">
      <div class="balloon balloon-1">🎈</div>
      <div class="balloon balloon-2">🎈</div>
    </div>
  </div>
</template>

<script>
import { getStudyRecords, getStoryPracticeRecords, syncRecords } from '@/api/book'
import { ensureWechatOpenid } from '@/utils/wechat-auth'

export default {
  name: 'StudyRecord',
  data() {
    return {
      records: [],
      storyRecords: [],
      user: null,
      syncStatus: false,
      loading: false,
      showAllWordRecords: false,
      wordPreviewLimit: 5
    }
  },
  computed: {
    displayedWordRecords() {
      return this.showAllWordRecords ? this.records : this.records.slice(0, this.wordPreviewLimit)
    },
    hiddenWordRecordCount() {
      return Math.max(this.records.length - this.wordPreviewLimit, 0)
    },
    studyDays() {
      const dates = new Set(this.allPracticeRecords.map(r => {
        const date = new Date(r.time)
        return `${date.getFullYear()}-${date.getMonth()}-${date.getDate()}`
      }))
      return dates.size
    },
    totalRecords() {
      return this.records.length + this.storyRecords.length
    },
    avgScore() {
      const scored = this.allPracticeRecords.filter(r => typeof r.score === 'number')
      if (scored.length === 0) return 0
      const total = scored.reduce((sum, r) => sum + r.score, 0)
      return Math.round(total / scored.length)
    },
    todayRecords() {
      const today = new Date().toDateString()
      return this.allPracticeRecords.filter(r => new Date(r.time).toDateString() === today)
    },
    streakDays() {
      // 简单计算连续天数
      return this.todayRecords.length > 0 ? 1 : 0
    },
    encourageText() {
      const texts = [
        '今天表现真棒，继续保持！',
        '学习使我快乐！',
        '又进步了一点点！',
        '加油，你是最棒的！'
      ]
      return texts[Math.floor(Math.random() * texts.length)]
    },
    allPracticeRecords() {
      const wordRecords = this.records.map(r => ({
        time: r.createTime,
        score: r.score
      }))
      const storyRecords = this.storyRecords.map(r => ({
        time: r.completedAt,
        score: r.totalScore
      }))
      return [...wordRecords, ...storyRecords]
    }
  },
  created() {
    this.loadRecords()
  },
  methods: {
    async loadRecords() {
      this.loading = true
      try {
        this.user = await ensureWechatOpenid(this.$route)
        // 先从 API 获取记录
        const [studyRes, storyRes] = await Promise.all([
          getStudyRecords(),
          getStoryPracticeRecords()
        ])
        const apiRecords = studyRes.data || []
        this.storyRecords = storyRes.data || []

        // 合并本地记录
        const stored = JSON.parse(localStorage.getItem('studyRecords') || '[]')
        const localRecords = stored.filter(r => !apiRecords.find(ar => ar.id === r.id))

        this.records = [...apiRecords, ...localRecords]
      } catch (error) {
        console.error('获取学习记录失败:', error)
        // 从 localStorage 加载
        const stored = localStorage.getItem('studyRecords')
        this.records = stored ? JSON.parse(stored) : []
        this.storyRecords = []
      } finally {
        this.loading = false
      }

      // 按时间倒序排列
      this.records.sort((a, b) => new Date(b.createTime) - new Date(a.createTime))
      this.storyRecords.sort((a, b) => new Date(b.completedAt) - new Date(a.completedAt))
      await this.syncToBackend()
    },
    getScoreColor(score) {
      if (score >= 90) return 'linear-gradient(135deg, #4CAF50 0%, #81C784 100%)'
      if (score >= 70) return 'linear-gradient(135deg, #FF9800 0%, #FFB74D 100%)'
      return 'linear-gradient(135deg, #F44336 0%, #E57373 100%)'
    },
    formatTime(timeStr) {
      const date = new Date(timeStr)
      const today = new Date()
      const yesterday = new Date(today)
      yesterday.setDate(yesterday.getDate() - 1)

      if (date.toDateString() === today.toDateString()) {
        return `今天 ${date.getHours().toString().padStart(2, '0')}:${date.getMinutes().toString().padStart(2, '0')}`
      } else if (date.toDateString() === yesterday.toDateString()) {
        return `昨天 ${date.getHours().toString().padStart(2, '0')}:${date.getMinutes().toString().padStart(2, '0')}`
      } else {
        return `${date.getMonth() + 1}/${date.getDate()} ${date.getHours().toString().padStart(2, '0')}:${date.getMinutes().toString().padStart(2, '0')}`
      }
    },
    clearAllRecords() {
      if (confirm('确定要清空所有学习记录吗？')) {
        localStorage.removeItem('studyRecords')
        this.records = []
      }
    },
    goToBooks() {
      this.$router.push('/books')
    },
    goToWordRecord(recordId) {
      this.$router.push(`/records/word/${recordId}`)
    },
    toggleWordRecords(event) {
      event.stopPropagation()
      this.showAllWordRecords = !this.showAllWordRecords
    },
    goToStoryRecord(studyRecordId) {
      this.$router.push(`/records/story/${studyRecordId}`)
    },
    async syncToBackend() {
      // 同步到后端
      const unsynced = this.records.filter(r => !r.synced)
      if (unsynced.length === 0) return

      try {
        await syncRecords(unsynced)
        // 标记为已同步
        this.records.forEach(r => {
          r.synced = true
        })
        localStorage.setItem('studyRecords', JSON.stringify(this.records))
      } catch (error) {
        console.error('同步失败:', error)
        return
      }
    }
  }
}
</script>

<style scoped>
.study-record-page {
  min-height: 100vh;
  background: linear-gradient(180deg, #E8F5E9 0%, #C8E6C9 100%);
  padding-bottom: 80px;
  position: relative;
  overflow-x: hidden;
}

/* 头部装饰 */
.header-decoration {
  position: relative;
  height: 100px;
  display: flex;
  justify-content: center;
  align-items: center;
}

.trophy {
  font-size: 50px;
  animation: bounce 2s ease-in-out infinite;
}

.star {
  position: absolute;
  font-size: 20px;
}

.star-1 {
  left: 20%;
  top: 30px;
  animation: twinkle 1.5s ease-in-out infinite;
}

.star-2 {
  right: 20%;
  top: 40px;
  animation: twinkle 1.5s ease-in-out infinite 0.5s;
}

@keyframes bounce {
  0%, 100% { transform: translateY(0); }
  50% { transform: translateY(-10px); }
}

@keyframes twinkle {
  0%, 100% { opacity: 0.3; }
  50% { opacity: 1; }
}

/* 统计概览 */
.stats-overview {
  padding: 0 15px 20px;
  text-align: center;
}

.page-title {
  font-size: 22px;
  color: #2E7D32;
  margin: 0 0 20px;
}

.stats-cards {
  display: flex;
  gap: 10px;
}

.stat-card {
  flex: 1;
  background: white;
  border-radius: 15px;
  padding: 15px 10px;
  display: flex;
  flex-direction: column;
  align-items: center;
  box-shadow: 0 4px 10px rgba(0,0,0,0.08);
}

.stat-card.highlight {
  background: linear-gradient(135deg, #FFD700 0%, #FFA500 100%);
  transform: scale(1.05);
}

.stat-icon-bg {
  font-size: 28px;
  margin-bottom: 8px;
}

.stat-content {
  display: flex;
  flex-direction: column;
  align-items: center;
}

.stat-value {
  font-size: 24px;
  font-weight: 700;
  color: #333;
}

.stat-card.highlight .stat-value {
  color: #333;
}

.stat-label {
  font-size: 12px;
  color: #888;
}

.stat-card.highlight .stat-label {
  color: #666;
}

/* 今日打卡 */
.check-in-section {
  padding: 0 15px 20px;
}

.section-header {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 12px;
}

.section-icon {
  font-size: 20px;
}

.section-header h2 {
  font-size: 16px;
  color: #333;
  margin: 0;
  flex: 1;
}

.record-count {
  font-size: 12px;
  color: #888;
}

.check-in-card {
  background: linear-gradient(135deg, #FFF9C4 0%, #FFECB3 100%);
  border-radius: 15px;
  padding: 20px;
  display: flex;
  align-items: center;
  gap: 20px;
  box-shadow: 0 4px 10px rgba(0,0,0,0.08);
}

.progress-ring {
  width: 80px;
  height: 80px;
  border-radius: 50%;
  background: conic-gradient(#FF6B6B v-bind('Math.min(todayRecords.length * 20, 100)')%, #FFE0B2 0);
  display: flex;
  align-items: center;
  justify-content: center;
}

.ring-inner {
  width: 65px;
  height: 65px;
  background: white;
  border-radius: 50%;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
}

.progress-value {
  font-size: 24px;
  font-weight: 700;
  color: #FF6B6B;
}

.progress-label {
  font-size: 11px;
  color: #888;
}

.check-in-info {
  flex: 1;
}

.encourage-text {
  font-size: 14px;
  color: #333;
  margin: 0 0 10px;
  font-weight: 500;
}

.streak-badge {
  display: inline-block;
  background: #FF6B6B;
  color: white;
  padding: 5px 12px;
  border-radius: 15px;
  font-size: 12px;
}

/* 历史记录 */
.history-section {
  padding: 0 15px;
}

.record-list {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.story-list {
  margin-top: 18px;
}

.list-title {
  display: flex;
  align-items: center;
  justify-content: space-between;
  font-size: 13px;
  font-weight: 600;
  color: #2E7D32;
  padding: 0 2px 2px;
}

.list-title span:last-child {
  color: #78909C;
  font-size: 12px;
  font-weight: 400;
}

.record-item {
  background: white;
  border-radius: 12px;
  padding: 15px;
  box-shadow: 0 2px 8px rgba(0,0,0,0.06);
  cursor: pointer;
}

.story-record {
  cursor: pointer;
}

.record-item.synced {
  border-left: 3px solid #4CAF50;
}

.record-main {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 10px;
}

.word-section {
  flex: 1;
}

.book-badge {
  display: inline-block;
  background: #E3F2FD;
  color: #1976D2;
  padding: 2px 8px;
  border-radius: 10px;
  font-size: 11px;
  margin-bottom: 5px;
}

.story-badge {
  background: #F3E5F5;
  color: #7B1FA2;
}

.word-title {
  font-size: 18px;
  color: #333;
  margin: 0;
}

.record-summary {
  font-size: 12px;
  color: #78909C;
  margin: 6px 0 0;
}

.score-badge {
  width: 45px;
  height: 45px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-size: 16px;
  font-weight: 700;
}

.line-detail-list {
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin: 10px 0;
}

.line-detail {
  background: #F8FBF8;
  border: 1px solid #E1F0E1;
  border-radius: 8px;
  padding: 10px;
}

.line-text {
  font-size: 14px;
  line-height: 1.5;
  color: #333;
  word-break: break-word;
}

.line-meta {
  display: flex;
  justify-content: space-between;
  gap: 8px;
  margin-top: 6px;
  font-size: 11px;
  color: #78909C;
  word-break: break-all;
}

.record-meta {
  display: flex;
  justify-content: space-between;
  font-size: 12px;
  color: #999;
  padding-top: 10px;
  border-top: 1px dashed #eee;
}

.toggle-list-btn {
  width: 100%;
  border: 1px solid #C8E6C9;
  background: rgba(255,255,255,0.8);
  color: #2E7D32;
  border-radius: 12px;
  padding: 12px;
  font-size: 14px;
  font-weight: 600;
}

.sync-status {
  color: #4CAF50;
}

/* 空状态 */
.empty-state {
  text-align: center;
  padding: 50px 20px;
}

.empty-illustration {
  font-size: 80px;
  margin-bottom: 15px;
}

.empty-state h3 {
  font-size: 18px;
  color: #333;
  margin: 0 0 10px;
}

.empty-state p {
  font-size: 14px;
  color: #888;
  margin: 0 0 20px;
}

.start-btn {
  background: #FF6B6B;
  color: white;
  border: none;
  padding: 12px 30px;
  border-radius: 25px;
  font-size: 14px;
  cursor: pointer;
  box-shadow: 0 4px 15px rgba(255,107,107,0.3);
}

/* 清空按钮 */
.clear-section {
  padding: 20px 15px;
  text-align: center;
}

.clear-btn {
  background: transparent;
  border: 1px solid #ddd;
  color: #999;
  padding: 10px 20px;
  border-radius: 20px;
  font-size: 13px;
  cursor: pointer;
}

/* 底部导航 */
.bottom-nav {
  position: fixed;
  bottom: 0;
  left: 0;
  right: 0;
  height: calc(56px + env(safe-area-inset-bottom, 0px));
  padding-bottom: env(safe-area-inset-bottom, 0px);
  background: white;
  display: flex;
  justify-content: space-around;
  align-items: center;
  box-shadow: 0 -1px 8px rgba(0, 0, 0, 0.06);
  z-index: 100;
}

.nav-item {
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: center;
  gap: 6px;
  min-width: 96px;
  padding: 8px 16px;
  cursor: pointer;
  color: #999;
}

.nav-icon {
  width: 20px;
  height: 20px;
  flex-shrink: 0;
}

.nav-text {
  font-size: 14px;
  line-height: 1;
  color: inherit;
}

.nav-item.active {
  color: #00b5ad;
}

.nav-item.active .nav-text {
  font-weight: 600;
}

/* 浮动装饰 */
.floating-elements {
  position: fixed;
  pointer-events: none;
}

.balloon {
  position: fixed;
  font-size: 30px;
  opacity: 0.3;
  animation: float-up 4s ease-in-out infinite;
}

.balloon-1 {
  bottom: 100px;
  left: 10px;
}

.balloon-2 {
  bottom: 150px;
  right: 10px;
  animation-delay: 2s;
}

@keyframes float-up {
  0%, 100% { transform: translateY(0); }
  50% { transform: translateY(-20px); }
}
</style>



// WEBPACK FOOTER //
// src/views/StudyRecord.vue